import clsx from 'clsx'
import AngleRight from '../pd-angle-right/pd-angle-right'
import PdMedia from '../pd-media/pd-media'
import { useMobileDetector } from '@/hooks/use-mobile-detector'
import PDModal from '../pd-modal'
import { useState } from 'react'
import useSWR from 'swr'
import { getContactUsData } from '@/service/contact-us.service'
import { useRouter } from 'next/router'
import { getLinkRelForHref } from '@/common/link-rel-helper'
export interface ContactUsSectionData {
  title?: string
  rows?: ContactUsSectionRow[]
}

interface ContactUsSectionRow {
  items?: ContactUsSectionItem[]
}

interface ContactUsSectionItem {
  label?: string
  value?: string
  iconUrl?: any
  iconDetailUrl?: any
  jumpUrlText?: string
  jumpUrl?: string
}

function getGridColsClass(columnCount?: number) {
  if (!columnCount) return 'laptop:grid-cols-1'
  if (columnCount % 3 === 0) return 'laptop:grid-cols-3'
  if (columnCount % 2 === 0) return 'laptop:grid-cols-2'
  return 'laptop:grid-cols-1'
}

export interface ContactUsSectionProps {
  data?: ContactUsSectionData
  infoHeader?: React.ReactNode
  infoFooter?: React.ReactNode | boolean
}
export function ContactUsSection(props: ContactUsSectionProps) {
  const { data, infoHeader, infoFooter } = props
  const router = useRouter()
  const locale = router.locale || 'en'
  const isMobileDevice = useMobileDetector()
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [detailImageUrl, setDetailImageUrl] = useState<string | null>(null)
  const shouldFetch = !data
  const fallbackContactUsData = data ?? getDefaultContactUsData()
  const { data: swrData } = useSWR<ContactUsSectionProps['data']>(
    shouldFetch ? getContactUsData.swrkey(locale) : null,
    () => getContactUsData(locale),
    {
      fallbackData: shouldFetch ? fallbackContactUsData : undefined
    }
  )

  // contact us event
  const contactUsData = data ?? swrData ?? getDefaultContactUsData()

  function handleOpenDetailImage(url?: string) {
    if (!isMobileDevice || !url) return
    setDetailImageUrl(url)
    setIsDetailModalOpen(true)
  }

  function handleCloseDetailImage() {
    setIsDetailModalOpen(false)
    setDetailImageUrl(null)
  }

  return (
    <section
      className={clsx(
        'w-full max-w-300 mx-auto',
        'flex flex-col laptop:flex-row laptop:justify-between',
        'px-6 laptop:px-0 gap-y-8 laptop:gap-y-0'
      )}
    >
      <h1
        id="contactUsTitle"
        className={clsx(
          'flex-1 min-w-0 text-24 laptop:text-36 font-semibold break-words laptop:w-75',
        )}
      >
        {contactUsData?.title}
      </h1>
      <div className={clsx('flex flex-col gap-y-6 laptop:gap-y-42px h-fit laptop:self-end laptop:ml-auto')}>
        {infoHeader}
        <div className={clsx('w-full flex flex-col gap-y-6 laptop:w-200 laptop:gap-y-6')}>
          {contactUsData?.rows?.map((row, index) => (
            <ul
              key={index}
              className={clsx('grid grid-cols-1 gap-y-6', 'laptop:gap-x-6', getGridColsClass(row?.items?.length))}
            >
              {row?.items?.map((item, idx) => (
                <li key={idx} className={clsx('flex flex-col')}>
                  <h2 className={clsx('text-14 font-semibold text-black/60')}>{item.label}</h2>
                  <div className={clsx('flex flex-wrap items-center gap-x-2')}>
                    <p className={clsx('inline text-18 laptop:text-24 font-semibold break-words text-wrap')}>{item.value}</p>
                    {item.iconUrl?.url && (
                      <div
                        className={clsx('relative w-6 h-6 group', isMobileDevice && 'cursor-pointer')}
                        onClick={() => handleOpenDetailImage(item.iconDetailUrl?.url)}
                      >
                        <PdMedia mask={false} src={item.iconUrl?.url} object="contain" />
                        {item.iconDetailUrl?.url && !isMobileDevice && <div
                          className={clsx(
                            'absolute -right-40 -top-40 z-10 h-40 w-40',
                            'rounded-8 bg-white shadow-lg ring-1 ring-black/10',
                            'origin-top-right scale-95 opacity-0 pointer-events-none',
                            'transition duration-200 ease-out',
                            'group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto'
                          )}
                        >
                          <PdMedia
                            mask={false}
                            src={item.iconDetailUrl?.url}
                            object="contain"
                          />
                        </div>}
                      </div>
                    )}
                  </div>

                  {item.jumpUrl && item.jumpUrlText && (
                    // eslint-disable-next-line react/jsx-no-target-blank
                    <a
                      href={normalizeExternalUrl(item.jumpUrl)}
                      className={clsx('text-14 text-pdblue flex items-center')}
                      target="_blank"
                      rel={getLinkRelForHref(normalizeExternalUrl(item.jumpUrl), 'noopener noreferrer', '_blank')}
                    >
                      {item.jumpUrlText}
                      <span className='inline-block mb-[2px]'> <AngleRight theme="PUDUBLUE" size='4x8' /></span>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ))}
        </div>
        {infoFooter}
      </div>
      <PDModal show={isDetailModalOpen && !!detailImageUrl} onClose={handleCloseDetailImage} cardBg={false}>
        <div className="w-full flex justify-center">
          <div className="w-full" style={{ aspectRatio: '1/1' }}>
            <PdMedia mask={false} src={detailImageUrl || ''} object="contain" />
          </div>
        </div>
      </PDModal>
    </section>
  )
}

function normalizeExternalUrl(url: string) {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

export function getDefaultContactUsData(): ContactUsSectionProps['data'] {
  return {
    title: 'Contact Us',
    rows: [],
  }
}
